import React, { useEffect, useState } from 'react';
import { Download as DownloadIcon, TrendingUp, Info } from 'lucide-react';

/**
 * How much this has actually been used.
 *
 * The ledger behind this has been recording a dated snapshot per day for a
 * while, and nothing rendered it, so the one question a reader most often
 * arrives with — has anybody used this? — had no answer anywhere on the site.
 *
 * Two rules govern what appears here. A counter is shown with who is asserting
 * it, because "this platform counting its own rows" and "a registry counting
 * for us" are worth different amounts. And a counter that has never been
 * recorded is absent rather than zero: a package that was never published and a
 * package nobody downloaded are different facts, and drawing them the same way
 * would be the more flattering of the two.
 */

interface Series {
  source: string;
  attested_by: string;
  points: Array<{ date: string; value: number }>;
}

interface AdoptionResponse {
  description?: string;
  series?: Record<string, Series>;
  days?: string[];
}

/** The counters worth a reader's attention, in the order they answer questions. */
const HEADLINE: Array<{ key: string; label: string; note: string }> = [
  {
    key: 'hub_downloads_total',
    label: 'Downloads of the runnable archive',
    note: 'Counted when the button on this site is pressed. Fetching the file directly is not counted, so this is a floor.'
  },
  {
    key: 'safety_events_total',
    label: 'Decisions recorded',
    note: 'Every action this deployment has weighed, including the ones it allowed.'
  },
  {
    key: 'agents_total',
    label: 'Agents under a profile',
    note: 'Agents with boundaries somebody accepted, on this deployment.'
  },
  {
    key: 'validation_runs_total',
    label: 'Pre-release checks run',
    note: 'Behavioural suites run against an agent before it was released.'
  }
];

/** A line of the series, drawn small. Values are labelled; the shape is context. */
const Spark: React.FC<{ points: Array<{ date: string; value: number }> }> = ({ points }) => {
  if (points.length < 3) return null;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const d = points
    .map((p, i) => `${(i / (points.length - 1)) * 100},${28 - ((p.value - min) / span) * 24}`)
    .join(' ');
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="mt-2 h-7 w-full" aria-hidden="true">
      <polyline
        points={d}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
        className="text-brand-500/70"
      />
    </svg>
  );
};

export const AdoptionPanel: React.FC = () => {
  const [data, setData] = useState<AdoptionResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/adoption')
      .then((res) => (res.ok ? (res.json() as Promise<AdoptionResponse>) : Promise.reject(new Error(String(res.status)))))
      .then((body) => { if (!cancelled) setData(body); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  if (failed) return null;

  const series = data?.series ?? {};
  const latest = (key: string) => {
    const points = series[key]?.points;
    if (!points || points.length === 0) return null;
    return points[points.length - 1].value;
  };
  const days = data?.days?.length ?? 0;

  return (
    <section className="glass-panel rounded-2xl p-5 sm:p-6">
      <h2 className="flex items-center gap-2 text-base font-extrabold text-slate-900 dark:text-white">
        <TrendingUp className="h-5 w-5 text-brand-600 dark:text-brand-400" />
        Has anybody used this?
      </h2>
      <p className="mt-1 max-w-3xl text-xs sm:text-sm text-slate-600 dark:text-slate-400">
        Counted once a day and kept, so the series is only as long as the ledger has been running
        {days > 0 ? ` — ${days} day${days === 1 ? '' : 's'} so far` : ''}. Counts only: no identities, no
        payloads, nothing about who you are.
      </p>

      <dl className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {HEADLINE.map((item) => {
          const value = latest(item.key);
          const points = series[item.key]?.points ?? [];
          return (
            <div
              key={item.key}
              className="flex flex-col rounded-xl border border-slate-200 bg-white/60 p-4 dark:border-slate-800 dark:bg-slate-900/40"
            >
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {item.label}
              </dt>
              <dd className="mt-1 text-2xl font-black tabular-nums text-slate-900 dark:text-white">
                {value === null ? '—' : value.toLocaleString()}
              </dd>
              <Spark points={points} />
              <p className="mt-2 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                {value === null ? 'Not recorded yet.' : item.note}
              </p>
            </div>
          );
        })}
      </dl>

      <div className="mt-4 flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-[11px] leading-5 text-slate-600 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-400">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          These are this project counting its own rows, which is worth less than a third party counting them.
          A download is not a user: the software reports nothing back once it is running, so nothing here can
          tell you whether it was kept. No outside organization has adopted it yet, and that is listed with
          everything else this project cannot prove.
        </span>
      </div>

      <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
        <DownloadIcon className="h-3 w-3" />
        The full dated series, with who attests each figure, is at{' '}
        <code className="rounded bg-slate-100 px-1 font-mono dark:bg-slate-800">/api/adoption</code>
      </p>
    </section>
  );
};
